import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@claritio/frontend/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors duration-[var(--duration-instant)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/85',
        outline: 'border border-input bg-card hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3 has-[>[data-stack]>span>svg:first-child]:px-3',
        sm: 'h-8 rounded-full gap-1.5 px-3',
        lg: 'h-10 rounded-full px-6',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

// Pass `loading` (even false) to make a button that can show work in progress: both labels share one grid
// cell, so the button keeps its width and position when "Save" turns into a spinner and "Saving…".
function Button({
  className, variant, size, asChild = false, loading, loadingText, disabled, children, ...props
}: React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean; loading?: boolean; loadingText?: React.ReactNode }) {
  const Comp = asChild ? Slot : 'button'
  if (asChild || loading === undefined) {
    return <Comp className={cn(buttonVariants({ variant, size, className }))} disabled={disabled} {...props}>{children}</Comp>
  }
  const layer = 'inline-flex items-center justify-center [gap:inherit] [grid-area:1/1]'
  return (
    <Comp className={cn(buttonVariants({ variant, size, className }), loading && 'disabled:opacity-100 cursor-progress')} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      <span data-stack className="inline-grid [gap:inherit]">
        <span className={cn(layer, loading && 'invisible')} aria-hidden={loading}>{children}</span>
        <span className={cn(layer, !loading && 'invisible')} aria-hidden={!loading}>
          <Loader2 className="animate-spin" />
          {loadingText ?? children}
        </span>
      </span>
    </Comp>
  )
}

export { Button, buttonVariants }
